import React from 'react';
import { crowBeeldkwaliteitScoreToConditionScore } from '../../../../common/src/domain/customInspectionTypes/crowBeeldkwaliteit/crowBeeldkwaliteitScoreToConditionScore';
import { LeftTrayItem, ScrollMenuItem } from '../../components/elements';
import { ISurvey } from '../../components/Surveys/Interfaces/ISurvey';
import { ISurveyTemplateElementGroupData } from '../../components/Surveys/ISurveyTemplateElementGroupData';
import { ISurveyTemplateManifestationData } from '../../components/Surveys/ISurveyTemplateManifestationData';
import { ISurveyTemplateUnitData } from '../../components/Surveys/ISurveyTemplateUnitData';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchJson } from '../../store/fetch';
import { ICustomInspectionTypeFrontEndSettings } from '../ICustomInspectionTypeFrontEndSettings';
import { CrowBeeldkwaliteitConditionScoreLabel } from './CrowBeeldkwaliteitConditionScoreLabel';
import { crowBeeldkwaliteitSelectOptions } from './crowBeeldkwaliteitSelectOptions';
import { SurveyDetailsCrowBeelkwaliteit } from './SurveyDetailsCrowBeeldkwaliteit';
import { SurveyDetailsCrowBeeldkwaliteitSummaryAndAdvice } from './SurveyDetailsCrowBeeldkwaliteitSummaryAndAdvice';
import { SurveyEditCrowBeeldkwaliteitResult } from './SurveyEditCrowBeeldkwaliteitResult';
import { SurveyEditCrowBeeldkwaliteitSummaryAndAdvice } from './SurveyEditCrowBeeldkwaliteitSummaryAndAdvice';

const tabNames = {
    crowBeeldkwaliteitResult: 'crowBeeldkwaliteitResult',
    summaryAndAdvice: 'summaryAndAdvice',
    manageVisuals: 'manageVisuals',
    videos: 'videos',
    download: 'download',
    workflow: 'workflow',
};

function SurveyEditPage(
    props: { tabName: string; surveyId: string; objectId: string; onItemChanged: () => Promise<void> } & (
        | ISurveyTemplateUnitData
        | ISurveyTemplateElementGroupData
        | ISurveyTemplateManifestationData
    ),
) {
    switch (props.tabName) {
        case tabNames.crowBeeldkwaliteitResult: {
            return <SurveyEditCrowBeeldkwaliteitResult {...props} />;
        }
        case tabNames.manageVisuals:
        case tabNames.videos:
        case tabNames.download:
        case tabNames.workflow:
        case tabNames.summaryAndAdvice:
            return null;
        default:
            return <h1>Unknown tab &quot;${props.tabName}&quot;</h1>;
    }
}

function SurveyDetailsLeftTrayListItems(props: { survey: ISurvey }) {
    const translate = useTranslate();
    return (
        <LeftTrayItem title={translate('crowBeeldkwaliteit.crowBeeldkwaliteitResult')}>
            <CrowBeeldkwaliteitConditionScoreLabel conditionScore={props.survey.condition} />
        </LeftTrayItem>
    );
}

function SurveyDetailsScrollMenuItems(props: { scrollTo: (name: string) => void }) {
    const translate = useTranslate();
    return (
        <>
            <ScrollMenuItem
                text={translate('crowBeeldkwaliteit.crowBeeldkwaliteit')}
                icon="fa fa-camera-retro"
                onClick={() => props.scrollTo('CrowBeeldkwaliteitReport')}
            />
            <ScrollMenuItem text="Samenvatting en advies" icon="fa fa-file-text-o" onClick={() => props.scrollTo('SummaryAndAdvice')} />
        </>
    );
}

function SurveyDetailsComponents(props: { survey: Required<ISurvey> }) {
    return (
        <>
            <SurveyDetailsCrowBeelkwaliteit surveyId={props.survey.id} />
            <SurveyDetailsCrowBeeldkwaliteitSummaryAndAdvice survey={props.survey} />
        </>
    );
}

function SurveyEditSummaryAndAdvice(props: { surveyId: string }) {
    return <SurveyEditCrowBeeldkwaliteitSummaryAndAdvice surveyId={props.surveyId} />;
}

function ConditionLabel(props: { conditionScore?: string }): JSX.Element | null {
    return <CrowBeeldkwaliteitConditionScoreLabel conditionScore={props.conditionScore} />;
}

export const crowBeeldkwaliteitFrontEndSettings: ICustomInspectionTypeFrontEndSettings = {
    tabNames,

    surveyEditTabs: [
        { name: tabNames.videos, textTranslateKey: 'survey.videosTab' },
        { name: tabNames.manageVisuals, textTranslateKey: 'crowBeeldkwaliteit.visualsAndDocuments' },
        { name: tabNames.crowBeeldkwaliteitResult, textTranslateKey: 'crowBeeldkwaliteit.crowBeeldkwaliteitResult' },
        { name: tabNames.workflow, textTranslateKey: 'workflow.workflow' },
        { name: tabNames.download, textTranslateKey: 'survey.downloads' },
        { name: tabNames.summaryAndAdvice, textTranslateKey: 'survey.summaryAndAdviceTab' },
    ],

    surveyEditDefaultTabName: tabNames.crowBeeldkwaliteitResult,
    showDocuments: true,
    objectTreeOptions: {
        hasElementsGroups: false,
        archiveLinkAvailable: false,
    },

    objectTreeDefaults: {
        filterOnElementGroups: false,
    },

    getSurveyEditPage: SurveyEditPage,
    getSurveyDetailsLeftTrayListItems: SurveyDetailsLeftTrayListItems,
    getSurveyDetailsScrollMenuItems: SurveyDetailsScrollMenuItems,
    getSurveyDetailsComponents: SurveyDetailsComponents,
    getSurveyEditSummaryAndAdvice: SurveyEditSummaryAndAdvice,
    getConditionLabel: ConditionLabel,

    showObjectTree: (tabName: string) => tabName === tabNames.manageVisuals || tabName === tabNames.crowBeeldkwaliteitResult,

    surveyEditLoadElements: async (surveyId: string) => {
        const elementsResponse = await fetchJson(`/api/surveys/${surveyId}/elements/crowBeeldkwaliteit/objectTree`, 'GET');
        const elements = elementsResponse.json;
        return {
            elements,
        };
    },

    getSurveyConditionFilterOptions: () =>
        crowBeeldkwaliteitSelectOptions.crowScore.map((i) => ({ value: crowBeeldkwaliteitScoreToConditionScore(i.value as any), text: i.text })),

    showLabelInSurveysMap: true,
};
